<template>
  <SetupLayout
    title="Lets Get Started"
    description="Welcome to MyQampus. Where you can automate your routine institute related work"
    :image="steps[currentStat - 1].image"
  >
    <UiTips :multi="true" :tips="SETUP_SLIDER_TIPS[currentStat]" />
    <ValidationObserver @submit="submitForm">
      <UIStepper :steps="steps" />
      <div v-if="currentStat === 1">
        <InputBox
          v-model="instituteData.institute_name"
          type="text"
          name="Institute Name"
          title="Institute Name"
          label="Institute Name"
          placeholder="Institute Name"
          rules="required"
        />
        <InputBox
          v-model.trim="instituteData.site_title"
          type="text"
          name="SITE_TITLE"
          title="SITE_TITLE"
          label="SITE_TITLE"
          placeholder="SITE_TITLE"
        />
        <div>
          <InputBox
            id="logo"
            v-model="logoFile"
            type="file"
            name="Logo"
            title="Logo"
            label="Logo"
            class="flex-1 border-none"
            :preview-image="previewImage"
            rules="size:500"
            :file-src="!!instituteLogo && !previewImage"
            placeholder="Choose File"
            :tooltip-prop="true"
            :height="13"
            :width="13"
            @preview="preview"
            @change="uploadLogo"
            @clearValue="clearSrcFileLogo"
          />
          <p class="text-xs -mt-3 mb-4 text-text-color-grey">
            Ideally image should be 500KB (jpeg/png/jpg)
          </p>
        </div>
        <div>
          <InputBox
            id="favicon"
            v-model="faviconFile"
            type="file"
            name="Favicon"
            title="Favicon"
            label="Favicon"
            :file-src="!!faviconIcon && !faviconPreviewImage"
            class="flex-1 border-none"
            :hidden="true"
            :preview-image="faviconPreviewImage"
            :rules="imageRules"
            placeholder="Choose File"
            :tooltip-prop="true"
            :height="13"
            :width="13"
            @preview="faviconPreview"
            @change="uploadFavicon"
            @clearValue="clearSrcFileFavicon"
          />
          <p class="text-xs -mt-3 mb-4 text-text-color-grey">
            Ideally image should be 500KB (jpeg/png/jpg)
          </p>
        </div>
        <UiSingleSelect
          v-model="defaultCountry"
          title="YOUR_COUNTRY"
          rules="required"
          label="title"
          :options="countrieslist"
        />

        <h3
          class="relative mt-4 sm:mt-2 pb-5 text-lg leading-8 font-semibold text-primary-gray-700 flex items-center gap-5"
        >
          <span>Multiple Campuses</span>
          <ToggleButton v-model="instituteData.multiple_campuses_allowed" :is-status-hide="true" />
        </h3>
      </div>

      <div v-if="currentStat === 2">
        <div class="flex-1">
          <InputBox
            v-if="systemSettings?.multiple_campuses_allowed"
            v-model="campusData.title"
            type="text"
            name="Campus Name"
            title="Campus Name"
            label="Campus Name"
            placeholder="Campus Name"
            rules="required"
          />
          <InputFieldWrapper>
            <UiTimePicker
              v-model="campusData.started_at_time"
              title="Start Time"
              type="time"
              name="Start Time"
              rules="required"
            />
            <UiTimePicker
              v-model="campusData.ended_at_time"
              title="End Time"
              type="time"
              name="End Time"
              rules="required"
            />
          </InputFieldWrapper>

          <InputBox
            v-model="classSection.title"
            type="text"
            name="Class Name"
            title="Class Name"
            label="Class Name"
            placeholder="Class Name"
            class="mt-6"
          />
          <InputBox
            v-model="classSection.section.title"
            type="text"
            name="Section"
            title="Section"
            label="Section"
            placeholder="Section"
          />
        </div>
      </div>
      <div v-if="currentStat === 3">
        <div class="flex-1 my-5">
          <div v-if="isLoading && !featureList.length" class="mt-28">
            <Loader :content="true" />
          </div>
          <div v-else class="space-y-8 max-w-3xl">
            <div class="w-full">
              <h3 class="text-lg font-semibold text-primary-gray-700 mb-1">
                Which student attendance you want
              </h3>
              <p class="text-sm text-primary-gray-500 mb-4">
                Enable one or more student attendance modes.
              </p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  v-for="item in studentAttendanceFeatures"
                  :key="item.name"
                  class="flex items-center justify-between gap-6 py-1 px-2 rounded-md hover:bg-gray-50"
                >
                  <span v-i18n="title" class="text-base text-primary-gray-700 capitalize">
                    {{ item.name }}
                  </span>
                  <ToggleButton
                    v-model="featureList[item.index].status"
                    :is-status-hide="true"
                    @change="toggleFeature(item.name, item.index)"
                  />
                </div>
              </div>
            </div>

            <div class="w-full">
              <h3 class="text-lg font-semibold text-primary-gray-700 mb-1 mt-2">
                Which staff attendance you want
              </h3>
              <p class="text-sm text-primary-gray-500 mb-4">
                Enable one or more staff attendance modes.
              </p>
              <div class="grid grid-cols-1 gap-4">
                <div
                  v-for="item in staffAttendanceFeatures"
                  :key="item.name"
                  class="flex items-center justify-between gap-6 py-1 px-2 rounded-md hover:bg-gray-50"
                >
                  <span v-i18n="title" class="text-base text-primary-gray-700 capitalize">
                    {{ item.name }}
                  </span>
                  <ToggleButton
                    v-model="featureList[item.index].status"
                    :is-status-hide="true"
                    @change="toggleFeature(item.name, item.index)"
                  />
                </div>
              </div>
            </div>

            <div class="w-full max-w-2xl flex items-center justify-between gap-6">
              <h3 class="text-lg font-semibold text-primary-gray-700 m-0">
                Do you need fee manager?
              </h3>
              <ToggleButton
                v-if="feeFeature"
                v-model="featureList[feeFeature.index].status"
                :is-status-hide="true"
                @change="toggleFeature(feeFeature.name, feeFeature.index)"
              />
            </div>

            <div class="w-full max-w-2xl flex items-center justify-between gap-6">
              <h3 class="text-lg font-semibold text-primary-gray-700 m-0">
                Do you need pickup center?
              </h3>
              <ToggleButton
                v-if="pickupFeature"
                v-model="featureList[pickupFeature.index].status"
                :is-status-hide="true"
                @change="toggleFeature(pickupFeature.name, pickupFeature.index)"
              />
            </div>
          </div>
        </div>
      </div>
      <template v-if="currentStat < 4">
        <div class="flex justify-between pt-1.5">
          <UIButton @click="signOut">Logout</UIButton>
          <UIButton button="primary">
            <div v-if="false">
              <Loader></Loader>
            </div>
            <div v-else>
              <span v-if="currentStat < 3">Next</span>
              <span v-else>Confirm</span>
            </div>
          </UIButton>
        </div>
      </template>
    </ValidationObserver>
  </SetupLayout>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import SetupLayout from '@router/layouts/setup-layout/SetupSliderLayout.vue'
import InputBox from '@components/UiElements/UiInputBox.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import AuthMixins from '@src/mixins/auth-mixin.js'
import { Form as ValidationObserver } from 'vee-validate'
import UIStepper from '@src/components/UiElements/UIStepper.vue'
import TabMixins from '@src/mixins/components/tab-mixin.js'
import UIButton from '@src/components/UiElements/UIButton.vue'
import RULE_CONSTANTS from '@src/constants/rule.constants.js'
import tooltipMixin from '@src/mixins/components/tooltip-mixin.js'
import UiTimePicker from '@components/UiElements/UiTimePicker.vue'
import InputFieldWrapper from '@src/components/UiElements/InputFieldWrapper.vue'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import ToggleButton from '@src/components/BaseComponent/toggleButton.vue'
import UiTips from '@src/components/UiElements/UiTips.vue'
import { objectDeepCopy } from '@src/utils/generalUtil'
import { getCountriesList } from '@src/utils/generalUtil.js'
import { getUTCOffsetByCountryCode, addTimeInCurrentTime } from '@src/utils/moment.util.js'
import DATETIME_CONSTANT from '@src/constants/date-time-constants.js'
import moment from 'moment'
import { MYQAMPUS_FEATURE_NAMES } from '@/src/constants/feature-name-constants.js'
import TIPS from '@/src/constants/tips.js'
export default {
  components: {
    SetupLayout,
    UIStepper,
    InputBox,
    Loader,
    ValidationObserver,
    UIButton,
    UiTimePicker,
    InputFieldWrapper,
    UiSingleSelect,
    ToggleButton,
    UiTips,
  },
  mixins: [AuthMixins, TabMixins, tooltipMixin],
  page: {
    title: 'Setup',
    meta: [{ name: 'description', content: 'Setup Slider for MyQampus' }],
  },

  data() {
    return {
      title: 'title',
      RULE_CONSTANTS,
      SETUP_SLIDER_TIPS: TIPS.SETUP_SLIDER,
      leftBar: 'leftBar',
      previewImage: null,
      faviconPreviewImage: null,
      searchQuery: '',
      countrieslist: getCountriesList(),
      steps: [
        {
          stat: 1,
          info: 'General Settings',
          active: true,
          markStatus: false,
          image: 'general settings',
        },
        {
          stat: 2,
          info: 'Campus, Class & Section',
          active: false,
          markStatus: false,
          image: 'campus class section',
        },
        { stat: 3, info: 'Preferences', active: false, markStatus: false, image: 'preferences' },
      ],
      faviconIcon: '',
      instituteLogo: '',
      logoFile: null,
      faviconFile: null,
      instituteData: {
        institute_name: '',
        site_title: '',
        multiple_campuses_allowed: false,
      },
      campusData: {
        title: 'Default',
        started_at_time: '',
        ended_at_time: '',
      },
      featureList: [],
      isLoading: false,
      classSection: {
        title: '',
        section: {
          title: '',
        },
      },
      generalSettings: {},
      defaultCountry: null,
    }
  },
  computed: {
    ...mapState('layout', ['systemSettings']),
    imageRules() {
      return `${RULE_CONSTANTS.SIZE}`
    },
    tableHeads() {
      return ['Feature Name', 'Status']
    },
    defaultDisabledFeatures() {
      return [
        MYQAMPUS_FEATURE_NAMES.STUDENTS_CAMPUS_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.STUDENTS_PERIOD_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.STAFF_CAMPUS_ATTENDANCE,
      ]
    },
    allFeatureNames() {
      return [
        MYQAMPUS_FEATURE_NAMES.STUDENTS_CAMPUS_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.STUDENTS_SECTION_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.STUDENTS_PERIOD_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.STUDENTS_DAILY_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.STAFF_CAMPUS_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.STAFF_DAILY_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.FEE_MANAGER,
        MYQAMPUS_FEATURE_NAMES.PICKUP_MANAGER,
      ]
    },
    studentAttendanceFeatures() {
      const names = [
        MYQAMPUS_FEATURE_NAMES.STUDENTS_CAMPUS_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.STUDENTS_SECTION_ATTENDANCE,
        MYQAMPUS_FEATURE_NAMES.STUDENTS_PERIOD_ATTENDANCE,
        // MYQAMPUS_FEATURE_NAMES.STUDENTS_DAILY_ATTENDANCE,
      ]
      return this.featureList
        .map((f, index) => ({ ...f, index }))
        .filter((f) => names.includes(f.name))
    },
    staffAttendanceFeatures() {
      const names = [
        MYQAMPUS_FEATURE_NAMES.STAFF_CAMPUS_ATTENDANCE,
        // MYQAMPUS_FEATURE_NAMES.STAFF_DAILY_ATTENDANCE,
      ]
      return this.featureList
        .map((f, index) => ({ ...f, index }))
        .filter((f) => names.includes(f.name))
    },
    feeFeature() {
      const idx = this.featureList.findIndex((f) => f.name === MYQAMPUS_FEATURE_NAMES.FEE_MANAGER)
      return idx > -1 ? { ...this.featureList[idx], index: idx } : null
    },
    pickupFeature() {
      const idx = this.featureList.findIndex(
        (f) => f.name === MYQAMPUS_FEATURE_NAMES.PICKUP_MANAGER,
      )
      return idx > -1 ? { ...this.featureList[idx], index: idx } : null
    },
  },
  watch: {
    defaultCountry: {
      handler(val) {
        this.instituteData.country_code = val.id
        this.instituteData.country_time_zone = this.getUTCOffsetByCountryCode(val.id)
        this.instituteData.default_timezone_region = val.zone
      },
    },
    'campusData.started_at_time': {
      handler(newVal) {
        if (newVal) {
          // Calculate 5 minutes after start time
          // Convert 24H format (HH:mm) to 12H format (hh:mm A) for the utility function
          const time12H = moment(newVal, DATETIME_CONSTANT.TIME_24_WITHOUT_SECONDS).format(
            DATETIME_CONSTANT.time12H,
          )
          const endTime12H = addTimeInCurrentTime(time12H, 'minutes', 5, DATETIME_CONSTANT.time12H)
          this.campusData.ended_at_time = moment(endTime12H, DATETIME_CONSTANT.time12H).format(
            DATETIME_CONSTANT.TIME_24_WITHOUT_SECONDS,
          )
        }
      },
    },
  },
  created() {
    this.setCurrentUserData()
    this.generalSettings = objectDeepCopy(this.$store.state.layout.generalSettings)
    this.defaultCountry = this.getCountryById(this.generalSettings.default_country)
  },
  methods: {
    getUTCOffsetByCountryCode,
    preview(image) {
      this.previewImage = image
    },
    faviconPreview(image) {
      this.faviconPreviewImage = image
    },
    getCountryById(id) {
      return this.countrieslist.find((country) => country.id === id)
    },
    clearSrcFileLogo() {
      this.previewImage = null
      this.logoFile = null
      this.instituteLogo = ''
    },
    clearSrcFileFavicon() {
      this.faviconPreviewImage = null
      this.faviconFile = null
      this.faviconIcon = ''
    },
    toggleFeature(feature, index) {
      const payload = { feature_name: feature }
      this.setDisabledFeatures(payload).catch(() => {
        this.featureList[index].status = false
      })
    },
    initializeFeatureList() {
      this.isLoading = true
      this.getDisabledFeatures()
        .then((response) => {
          const fetchedFeatureNames =
            response?.disabled_features?.map((item) => item.feature_name) || []
          this.featureList = this.allFeatureNames.map((name) => ({
            name,
            status: !this.defaultDisabledFeatures.includes(name),
          }))
          this.defaultDisabledFeatures.forEach((featureName) => {
            if (!fetchedFeatureNames.includes(featureName)) {
              const index = this.featureList.findIndex((f) => f.name === featureName)
              if (index > -1) {
                this.toggleFeature(featureName, index)
              }
            }
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    submitForm() {
      if (this.currentStat === 1) {
        this.handleInstituteSetup()
      } else if (this.currentStat === 2) {
        this.handleCampusSetup()
      } else if (this.currentStat === 3) this.goToDashboard()
    },
    handleInstituteSetup() {
      this.instituteSetup(this.instituteData).then((response) => {
        this.updaateMultipleCampusesAllowed(this.instituteData.multiple_campuses_allowed)
        if (this.instituteData?.institute_name) {
          this.$store.commit('settings/GS_SET_INSTITUTE_NAME', this.instituteData.institute_name)
          this.$store.commit('settings/GS_SET_SITE_TITLE', this.instituteData.site_title)
          this.$store.commit('layout/SET_GENERAL_SETTINGS', response?.data)
          localStorage.setItem('instituteTitle', JSON.stringify(this.instituteData.institute_name))
        }
        this.nextStep(1)
      })
    },
    handleCampusSetup() {
      this.campusSetup(this.campusData).then((response) => {
        // Update Campus Count
        let stats = JSON.parse(localStorage.getItem('institutesCounts') || {})
        if (stats.length) {
          stats.campuses_count = (stats.campuses_count || 0) + 1
          localStorage.setItem('institutesCounts', JSON.stringify(stats))
        }

        if (this.classSection.title === '' && this.classSection.section.title === '') {
          this.nextStep(2)
        } else {
          this.sectionSetup(this.classSection).then(() => {
            this.nextStep(2)
          })
        }

        this.getCampusList().then((response) => {
          if (!this.systemSettings?.multiple_campuses_allowed)
            this.$store.commit('layout/SET_SELECTED_CAMPUSE', response?.data?.[0], { root: true })
        })

        this.initializeFeatureList()
      })
    },

    updaateMultipleCampusesAllowed(multiple_campuses_allowed) {
      const systemSettings = objectDeepCopy(this.systemSettings)
      systemSettings.multiple_campuses_allowed = multiple_campuses_allowed
      this.$store.commit('layout/SET_INSTITUE_SETTINGS', systemSettings)
    },
    uploadLogo(file) {
      const formData = new FormData()
      formData.append('image', file)
      this.setInstituteLogo({ payload: formData }).then((response) => {
        this.instituteLogo = response?.data?.image
        this.$store.commit('settings/GS_SET_LOGO', response?.data?.image)
      })
    },

    uploadFavicon(file) {
      const formData = new FormData()
      formData.append('favicon', file)
      this.setInstituteFaviconIcon({ payload: formData }).then((response) => {
        this.faviconIcon = response?.data?.favicon
        this.$store.commit('settings/GS_SET_FAVICON', response?.data?.favicon)
      })
    },

    goToDashboard() {
      localStorage.setItem('signUpSlider', false)
      localStorage.removeItem('institutesCounts')
      localStorage.removeItem('institutesTitle')
      this.$router?.push({ name: 'quick-actions' })
    },
    setCurrentUserData() {
      this.instituteData.institute_name = this.systemSettings?.title
      this.instituteData.site_title = this.systemSettings?.site_title?.site_title
      this.instituteData.multiple_campuses_allowed = this.systemSettings?.multiple_campuses_allowed
    },
    ...mapActions('institute', [
      'instituteSetup',
      'campusSetup',
      'sectionSetup',
      'setInstituteLogo',
      'setInstituteFaviconIcon',
    ]),
    ...mapActions('layout', ['getCampusList', 'setDisabledFeatures', 'getDisabledFeatures']),
  },
}
</script>

<style lang="scss" module>
@import '@src/router/views/auth/auth.scss';
</style>
